import { DateTime } from "@bps/date-time";
import { guid } from "@libs/common/guid";

export interface PracticeLocation {
  id: guid;
  locationName: string;
  timeZoneId: string;
  bookingSettings: {
    enabled: boolean;
  };
  tenantId: string;
  providers?: PracticeLocationProvider[];
  exclusionPeriods?: PracticeLocationExclusions[];
  acceptNewPatientsEnabled: boolean;
  address: string;
  allowCancellationsEnabled: boolean;
  city: string;
  email: string;
  createNewPatientRecord: boolean;
  maximumAppointmentsPerDay: number;
  minimumTImeTocancel: number;
  minimumTimeToBook: number;
  orgUnitName: string;
  phone: string;
  postCode: string;
  sequenceNo: number;
  showAvailability: number;
  street1: string;
  suburb: string;
  logoUrl: string;
  patientRecordConfig: string;
}

export interface PracticeLocationExclusions {
  providerId: string;
  locationId: string;
  providerName?: string;
  exclusionPeriodName?: string;
  startDate?: DateTime;
  endDate?: DateTime;
  startTime: number;
  endTime: number;
  recurrenceRule: RecurrenceRule;
}

export enum RecurrenceRuleFrequency {
  Daily = 0,
  Weekly = 1
}
export interface RecurrenceRule {
  frequency: RecurrenceRuleFrequency;
  interval: number;
  recurringDays: RecurrenceRuleRecurringDay[];
}

export enum RecurrenceRuleRecurringDay {
  Monday = 0,
  Tuesday = 1,
  Wednesday = 2,
  Thursday = 3,
  Friday = 4,
  Saturday = 5,
  Sunday = 6
}

export enum ExclusionType {
  Location = 0,
  Provider = 1,
  Unknown = 999
}

export interface PracticeLocationProvider {
  providerId: string;
  tenantId: string;
  locationId: string;
  providerOnlineStatus: string;
  displayName: string;
  tagLine: string;
  firstAvailable?: DateTime;
  lastAvailable?: DateTime;
  availabilityCount: 0;
}

export interface ProviderAppointmentType {
  providerId: string;
  appointmentTypeId: string;
  locationId: string;
  name: string;
  newPatientDuration: number;
  existingPatientDuration: number;
  providerNewPatient: boolean;
  providerExistingPatient: boolean;
  existingPatientSupportingProvider: boolean;
  newPatientSupportingProvider: boolean;
  locationNewPatients: boolean;
  locationExistingPatients: boolean;
  callClinic: boolean;
  teleHealth: boolean;
  customAvailabilityCount: number;
}

export interface PracticeLocationUpdate {
  onlineBookingsEnabled: boolean;
  createNewPatientRecord: boolean;
  allowCancellationsEnabled: boolean;
  maximumAppointmentsPerDay: number | string;
  minimumTimeToBook: number | string;
  showAvailability: number | string;
}

export interface PracticeLocationDto
  extends Omit<
    PracticeLocation,
    "bookingSettings" | "providers" | "exclusionPeriods"
  > {
  onlineBookingsEnabled: boolean;
  providers?: (Omit<
    PracticeLocationProvider,
    "firstAvailable" | "lastAvailable"
  > & {
    firstAvailable?: string;
    lastAvailable?: string;
  })[];
  exclusionPeriods?: (Omit<
    PracticeLocationExclusions,
    "startDate" | "endDate"
  > & {
    startDate?: string;
    endDate?: string;
  })[];
}

export interface AppointmentStats {
  tenantId: string;
  lastCreated?: DateTime;
  todayCount: number;
  yesterdayCount: number;
  lastSevenDaysCount: number;
  previousSevenDayCount: number;
  thisMonthCount: number;
  previousMonthCount: number;
  thisYearCount: number;
  previousYearCount: number;
}

export interface LocationAppointmentStats extends AppointmentStats {
  locationId: string;
  locationName: string;
}

export interface AppointmentStatsDto
  extends Omit<AppointmentStats, "lastCreated"> {
  lastCreated: string;
}

export interface LocationAppointmentStatsDto
  extends Omit<LocationAppointmentStats, "lastCreated"> {
  lastCreated: string;
}

export interface Provider {
  providerId: string;
  tenantId: string;
  firstName: string;
  lastName: string;
  title: string;
  hasAppointments: boolean;
  isDeleted: boolean;
  firstAvailable?: DateTime;
  lastAvailable?: DateTime;
  availabilityCount: number;
  appointmentTypes: ProviderAppointmentType[];
}

export interface ProviderDto
  extends Omit<Provider, "firstAvailable" | "lastAvailable"> {
  firstAvailable?: string;
  lastAvailable?: string;
}

export interface EntitySequence {
  tenantId: guid;
  entityType: string;
  sequenceNo: number;
  rowCount?: number;
  updatedDate?: DateTime;
}
